import { ReactNode } from 'react';
import { formatMoney, formatNumber } from 'accounting';
import classNames from 'classnames';

import { InfoTooltip, LinkAnchor } from '@hcs/design-system';
import { useNavigationContext } from '@hcs/navigation';
import { useOrgSelfServeInfo } from '@hcs/self-serve-info';
import { CapabilityKeys } from '@hcs/types';

import styles from './OrgProductPricing.module.css';
export interface OrgPricingProductProps {
  className?: string;
  capabilityKey: CapabilityKeys;
}

interface OrgPricingProps {
  className?: string;
  unitPrice: number;
  unitDescriptor: string;
  unitDescriptorPlural: string;
  includedInTier: number;
  disclaimer?: ReactNode;
  actionDescriptor: string;
}

export const OrgProductPricing = ({
  className,
  unitPrice,
  unitDescriptor,
  unitDescriptorPlural,
  includedInTier,
  disclaimer,
  actionDescriptor,
}: OrgPricingProps) => {
  const { data: orgSsInfo } = useOrgSelfServeInfo();
  const { billingDashboardLink, usageReportLink } = useNavigationContext();
  return (
    <div className={classNames(styles.OrgProductPricing, className)}>
      <div>
        {includedInTier ? (
          <>
            <b>{formatNumber(includedInTier)}</b> {unitDescriptorPlural} per
            month are included at no extra cost with your{' '}
            <b>{orgSsInfo?.self_serve_plan?.name}</b> plan. After{' '}
            <b>{formatNumber(includedInTier)}</b>, you will be charged{' '}
            <b>{formatMoney(unitPrice)}</b> for each additional{' '}
            {`${unitDescriptor}${actionDescriptor ? ` ${actionDescriptor}` : ''}`}
            .
          </>
        ) : (
          <>
            You will be charged <b>{formatMoney(unitPrice)}</b> per{' '}
            {unitDescriptor}.
          </>
        )}{' '}
        {disclaimer ? <>{disclaimer} </> : null}
      </div>
      <div>
        See your{' '}
        <LinkAnchor
          dataHcName="billing-dashboard-link"
          to={billingDashboardLink || ''}
          target="_blank"
          rel="noreferrer"
        >
          Billing Dashboard
        </LinkAnchor>{' '}
        for details.
        <InfoTooltip
          dataHcName="billing-dashboard-info"
          description={
            <>
              Note the “Last Updated” time on the Billing Dashboard. If you need
              more up-to-date information, create a{' '}
              <LinkAnchor
                dataHcName="usage-report-link"
                to={usageReportLink || ''}
                target="_blank"
                rel="noreferrer"
              >
                Usage Report
              </LinkAnchor>
            </>
          }
        />
      </div>
    </div>
  );
};
